import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  createAppointment,
  listAppointments,
} from "../redux/actions/appointmentActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { fetchFiles } from "../redux/actions/fileActions";
import CustomAutocomplete from "../components/CustomAutocomplete";
import FileAutocomplete from "../components/FileAutocomplete";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/esm/format";
import parse from "date-fns/esm/parse";
import startOfWeek from "date-fns/esm/startOfWeek";
import getDay from "date-fns/esm/getDay";
import enUS from "date-fns/esm/locale/en-US";
import "./customStyles.css";

import {
  Container,
  Autocomplete,
  TextField,
  Button,
  MenuItem,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Breadcrumbs,
  CircularProgress,
  Link,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { textFieldStyles } from "../styles/textFieldStyles";
import BreadcrumbNav from "../components/BreadcrumbNav";
import SubmitButton from "../components/SubmitButton";
import CancelButton from "../components/CancelButton";
import enGB from "date-fns/esm/locale/en-GB";
import { nl } from "date-fns/locale";
const locales = {
  "en-US": enUS,
  "nl": nl,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Breadcrumbs will be defined inside component to access t function
const buttonStyles = {
  borderRadius: 1, // Rounded corners
  padding: "6px 16px", // Custom padding
  textTransform: "none", // Disable uppercase text
  boxShadow: "none", // Remove default shadow
  "&:hover": {
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)", // Add custom shadow on hover
  },
};

const appointmentTypes = [
  "pretrialHearing",
  "arraignmentHearing",
  "motionHearing",
  "sentencingHearing",
  "appealHearing",
];

const CreateAppointmentPage = () => {
  const { t, i18n } = useTranslation(["pages", "forms", "buttons", "common"]);

  const appointmentColours = [
    { name: t("common:colors.red"), color: "#FF0000" },
    { name: t("common:colors.yellow"), color: "#FFFF00" },
    { name: t("common:colors.green"), color: "#008000" },
    { name: t("common:colors.blue"), color: "#0000FF" },
    { name: t("common:colors.no_color"), color: "#FFFFFF" },
  ];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { relationships } = useSelector((state) => state.relationshipList);
  const { files } = useSelector((state) => state.fileList);
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state
  const { appointments } = useSelector((state) => state.appointmentList);

  const [owner, setOwner] = useState("");
  const [type, setType] = useState("");
  const [calendarType, setCalendarType] = useState(t("common:hearing"));
  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: t("common:home"), href: "/" },
    { label: t("common:files"), href: "/files" },
    { label: t("common:calendar"), href: "/calendar" },
  ]);

  const [caseId, setCaseId] = useState(() => {
    // Check location state first
    const locationCaseId = location.state?.caseId;
    const initialCaseFile =
      files?.find((file) => file._id === locationCaseId) || null;

    if (initialCaseFile) {
      setBreadcrumbs((prevBreadcrumbs) => [
        ...prevBreadcrumbs,
        {
          label: initialCaseFile.name || t("common:case_file"),
          href: `/files/${initialCaseFile._id}/calendar`,
        },
      ]);
    }
    if (locationCaseId) return locationCaseId;

    // Then check localStorage
    const storedCaseId = localStorage.getItem("selectedCaseId");
    if (storedCaseId) return storedCaseId;

    // Default to empty string if neither exists
    return "";
  });
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [reminder, setReminder] = useState("");
  const [locationField, setLocationField] = useState("");
  const [colour, setColour] = useState(t("common:colors.no_color"));
  const [userRole, setUserRole] = useState(null);
  const [userRelationship, setUserRelationship] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    if (user) {
      setUserRole(user.role);
      setOwner(user.relationship);
    }
  }, [user]);

  useEffect(() => {
    dispatch(fetchRelationships());
    dispatch(fetchFiles());

    dispatch(listAppointments());
  }, [dispatch]);

  const { loading, success, error } = useSelector(
    (state) => state.appointmentCreate
  );

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (success) {
      setNotification({
        open: true,
        message: t("messages:confirmations.hearingCreated"),
        severity: "success",
      });
      dispatch({ type: "APPOINTMENT_CREATE_RESET" }); // Reset the success state
      dispatch(fetchFiles());
      setTimeout(() => {
        navigate(-1);
      }, 1000);
      // window.location.reload(true);
      // navigate('/documents');
    } else if (error) {
      setNotification({
        open: true,
        message: error,
        severity: "error",
      });
      dispatch({ type: "APPOINTMENT_CREATE_RESET" }); // Reset the error state
    }
  }, [success, error, dispatch]);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newAppointment = {
      owner,
      type,
      case: caseId,
      calendarType,
      subject,
      description,
      startDate,
      endDate,
      reminder,
      location: locationField,
      colour,
    };

    dispatch(createAppointment(newAppointment));
  };

  const handleCancel = () => {
    setSnackbarMessage(t("messages:confirmations.appointmentCreationCancelled"));
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
    navigate("/calendar");
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const inputStyles = {
    width: "30%",
    marginBottom: 2,
  };

  // Filter files based on user role
  const filteredFiles = files.filter((file) => {
    if (user.role === "Manager") {
      return true; // Managers can see all files
    } else if (user.role === "Responsible Lawyer") {
      return file.officeMembers.some((member) => {
        return (
          member.member._id === user.relationship &&
          member.role === "Responsible Lawyer"
        );
      });
    } else if (user.role === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  const [filteredAppointments, setFilteredAppointments] = useState([]);

  useEffect(() => {
    const filtered = appointments
      .map((appointment) => ({
        ...appointment,
        isNew: false,
      }))
      .filter(
        (appointment) =>
          new Date(appointment.startDate).toDateString() ===
            new Date(startDate).toDateString() &&
          appointment.owner?._id.toString() === user?.relationship.toString()
      );

    if (startDate && endDate) {
      const newAppointment = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        owner: user?.relationship,
        isNew: true, // Indicate that this is a new appointment
        // Add other necessary fields for the new appointment
      };
      console.log(newAppointment);
      filtered.push(newAppointment);
    }

    setFilteredAppointments(filtered);
  }, [startDate, endDate, appointments]);

  useEffect(() => {
    const filteredEventDivs = document.querySelectorAll(
      "div.rbc-event-filtered"
    );
    console.log("Filtered Event Divs:", filteredEventDivs);
  }, [filteredAppointments]); // Ensure this runs after filteredAppointments are updated

    // Custom date formatting function
    const formatCustomDate = (date) => {
      const dateObj = new Date(date);
      const weekday = dateObj.toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-US', { weekday: 'long' });
      const day = dateObj.getDate();
      const month = dateObj.toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-US', { month: 'long' });
      const year = dateObj.getFullYear();
      
      return `${weekday}, ${day} ${month}, ${year}`;
    };

  const CustomToolbar = () => (
    <div className="rbc-toolbar">
      <span className="rbc-toolbar-label">
        {startDate
          ? formatCustomDate(startDate)
          : formatCustomDate(new Date())}
      </span>
    </div>
  );

  const convertToDate = (date) => {
    return typeof date === "string" ? new Date(date) : date;
  };

  const formats = {
    timeGutterFormat: (date, culture, localizer) =>
      localizer.format(date, "HH:mm", culture),
    eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, "HH:mm", culture)} - ${localizer.format(
        end,
        "HH:mm",
        culture
      )}`,
  };

  const eventPropGetter = (event) => {
    const className = event.isNew ? "rbc-event-new" : "rbc-event-filtered";
    return { className };
  };

  const shouldDisableMinutes = (minutes) => {
    return minutes % 15 !== 0;
  };

  useEffect(() => {
    const handlePopupOpen = () => {
      setTimeout(() => {
        const disabledMenuItems = document.querySelectorAll(
          "li.MuiButtonBase-root.MuiMenuItem-root.Mui-disabled"
        );

        disabledMenuItems.forEach((item) => {
          // Perform your actions here
          item.classList.add("hidden-option"); // Example: Add a class to hide the element
        });
      }, 100);
    };

    document.addEventListener("click", handlePopupOpen);

    return () => {
      document.removeEventListener("click", handlePopupOpen);
    };
  }, []);

  useEffect(() => {
    resetScrollTop();
  }, [startDate, endDate]);

  const resetScrollTopWait = () => {
    setTimeout(() => {
      const secondClockSection = document.querySelector(
        ".MuiMultiSectionDigitalClockSection-root:nth-of-type(2)"
      );
      const thirdClockSection = document.querySelector(
        ".MuiMultiSectionDigitalClockSection-root:nth-of-type(3)"
      );

      if (secondClockSection) {
        secondClockSection.scrollTop = 0;
      }
      if (thirdClockSection) {
        thirdClockSection.scrollTop = 0;
      }
    }, 100);
  };

  const resetScrollTop = () => {
    const secondClockSection = document.querySelector(
      ".MuiMultiSectionDigitalClockSection-root:nth-of-type(2)"
    );
    const thirdClockSection = document.querySelector(
      ".MuiMultiSectionDigitalClockSection-root:nth-of-type(3)"
    );

    if (secondClockSection) {
      secondClockSection.scrollTop = 0;
    }
    if (thirdClockSection) {
      thirdClockSection.scrollTop = 0;
    }
  };

  // Usage
  useEffect(() => {
    resetScrollTop();
  }, []);

  return (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={t("pages:hearings.create_title")}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />

      <Box
        sx={{
          backgroundColor: "white",
          padding: "30px 24px",
          marginTop: "24px",
          width: "95%",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <style>
          {`
          .hidden-option {
            display: none !important;
          }
            /* Custom scrollbar styles */
          .MuiMultiSectionDigitalClockSection-root:first-of-type::-webkit-scrollbar {
            width: 6px; /* Adjust the width of the scrollbar */
          }
            .MuiMultiSectionDigitalClockSection-root:nth-of-type(2),
    .MuiMultiSectionDigitalClockSection-root:nth-of-type(3) {
      overflow: hidden; /* Disable the scrollbar */
    }
          .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.2); /* Adjust the color of the scrollbar thumb */
            border-radius: 0; /* Adjust the roundness of the scrollbar thumb */
          }
          .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar-track {
            background-color: transparent; /* Adjust the color of the scrollbar track */
          }
        `}
        </style>
        <form onSubmit={handleSubmit} style={{ width: "80%" }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ width: "100%" }}>
              <FormControl variant="standard" sx={{ mr: 3, width: "44.5%" }}>
                <CustomAutocomplete
                  options={relationships}
                  value={
                    relationships?.find((rel) => rel._id === owner) || null
                  }
                  onChange={(event, newValue) =>
                    setOwner(newValue ? newValue._id : "")
                  }
                  label={t("common:owner")}
                  filterType="Member"
                  addNewLabel={t("buttons:primary.addOwner")}
                  addNewPath="/relationships/create"
                />
              </FormControl>
            </Box>
            <Box sx={{ width: "98.5%", display: "flex" }}>
              <Autocomplete
                options={appointmentTypes}
                getOptionLabel={(option) => {
                  const translationKey = `forms:legal.appointmentTypes.${option}`;
                  const translation = t(translationKey);
                  // Fallback to the option itself if translation is not found
                  return translation !== translationKey ? translation : option;
                }}
                value={type}
                onChange={(event, newValue) => setType(newValue || "")}
                renderInput={(params) => (
                  <TextField {...params} label={t("common:type")} required />
                )}
                sx={textFieldStyles({ width: "45%" })}
                size="small"
              />

              <FormControl sx={{ width: "45%" }}>
                <FileAutocomplete
                  options={filteredFiles}
                  value={
                    filteredFiles.find((file) => file._id === caseId) || null
                  }
                  onChange={(event, newValue) =>
                    setCaseId(newValue ? newValue._id : "")
                  }
                  label={t("common:case")}
                  name="case"
                />
              </FormControl>
            </Box>

            <TextField
              label={t("forms:labels.subject")}
              value={subject}
              required
              onChange={(e) => setSubject(e.target.value)}
              sx={textFieldStyles({ width: "91.5%" })}
              size="small"
            />
            <TextField
              label={t("common:description")}
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={textFieldStyles({ width: "91.5%" })}
              size="small"
            />
            <Box
              sx={{
                width: "95%",
                display: "flex",
                flexDirection: "row",
              }}
            >
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={i18n.language === 'nl' ? nl : enGB}
              >
                <DateTimePicker
                  value={startDate}
                  size="small"
                  required
                  onChange={(newValue) => (
                    setStartDate(newValue), setEndDate(newValue)
                  )}
                  onOpen={resetScrollTopWait}
                  inputFormat="dd/MM/yyyy HH:mm"
                  renderInput={(params) => (
                    <TextField {...params} fullWidth required size="small" />
                  )}
                  slotProps={{
                    textField: {
                      fullWidth: true,

                      sx: textFieldStyles({ width: "30%" }),
                      size: "small",
                      label: t("forms:labels.startDate"),
                    },
                  }}
                  minutesStep={15}
                  ampm={false}
                />
                <DateTimePicker
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  onOpen={resetScrollTopWait}
                  inputFormat="dd/MM/yyyy HH:mm"
                  renderInput={(params) => (
                    <TextField {...params} fullWidth required size="small" />
                  )}
                  slotProps={{
                    textField: {
                      fullWidth: true,

                      sx: textFieldStyles({ width: "30%" }),
                      size: "small",
                      label: t("forms:labels.endDate"),
                    },
                  }}
                  minutesStep={15}
                  ampm={false}
                />
              </LocalizationProvider>
              <TextField
                label={t("forms:placeholders.reminder_days_before")}
                type="number"
                fullWidth
                value={reminder}
                onChange={(e) => setReminder(e.target.value)}
                sx={textFieldStyles({ width: "30%" })}
                size="small"
              />
            </Box>

            <TextField
              label={t("forms:location")}
              value={locationField}
              onChange={(e) => setLocationField(e.target.value)}
              sx={textFieldStyles({ width: "91.5%" })}
              size="small"
            />
            <FormControl sx={textFieldStyles({ width: "44.5%" })} size="small">
              <InputLabel id="colour-select-label">{t("forms:colour")}</InputLabel>
              <Select
                labelId="colour-select-label"
                value={colour}
                onChange={(e) => setColour(e.target.value)}
                label={t("forms:colour")}
              >
                {appointmentColours.map((appointmentColour) => (
                  <MenuItem
                    key={appointmentColour.name}
                    value={appointmentColour.name}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          backgroundColor: appointmentColour.color,
                          marginRight: 1,
                        }}
                      />
                      {appointmentColour.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              // padding: "10px 16px",
              // background: "#fff",
              zIndex: 10,
              backgroundColor: "white",
              color: "#000000",
              padding: "13px 80px",
            }}
          >
            <CancelButton
              variant="outlined"
              onClick={handleCancel}
              size="small"
            >
              {t("buttons:secondary.cancel")}
            </CancelButton>
            <SubmitButton onClick={handleSubmit} size="small">
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                t("buttons:primary.createHearing")
              )}
            </SubmitButton>
          </Box>
        </form>
        <Box sx={{ width: "30%" }}>
          <Calendar
            localizer={localizer}
            culture={i18n.language === 'nl' ? 'nl' : 'en-US'}
            events={filteredAppointments.map((event) => ({
              ...event,
              startDate: convertToDate(event.startDate),
              endDate: convertToDate(event.endDate),
            }))}
            startAccessor="startDate"
            endAccessor="endDate"
            eventPropGetter={eventPropGetter}
            style={{ height: 500 }}
            views={["day"]}
            defaultView="day"
            date={startDate ? startDate : new Date()} // Check if startDate is empty
            components={{
              toolbar: CustomToolbar,
            }}
            formats={formats}
          />
        </Box>
      </Box>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateAppointmentPage;
